import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Transient confirmations at the bottom of the screen.
 *
 * Every list mutation says what it did, because several of them happen far from
 * where the user is looking — a keyboard undo, a removal from a day view, an
 * add from a card two hundred rows down. One toast at a time: a queue would
 * stack up during a burst of clicks and outlive the action it describes.
 *
 * Each toast lives 5 seconds and then fades out over 400ms.
 */

const LIFETIME_MS = 5000;
const FADE_MS = 400;

export type ToastAction =
  /** "Go to My Schedule ↗" — deep-links to the day this event landed on. */
  | { kind: 'goToSchedule'; eventId: string; dayKey: string }
  | { kind: 'undo' }
  /** Undo for "Not Interested", which isn't part of the lists' history. */
  | { kind: 'unhide'; eventId: string };

/**
 * A message built around one title: `before` + title + `after`, with any
 * quotes in `before` and `after`.
 *
 * Kept in parts so a phone can shorten the title and never the words around
 * it. “Perspectives on Brooklyn's…” reads as nothing; “Perspectives on
 * Brooklyn's Evolving…” added to My Schedule. says what happened.
 */
export interface TitledMessage {
  before: string;
  title: string;
  after: string;
}

export type ToastMessage = string | TitledMessage;

/** The common case: “title” followed by what happened to it. */
export function quoted(title: string, after: string): TitledMessage {
  return { before: '“', title, after: `” ${after}` };
}

export function messageText(message: ToastMessage): string {
  return typeof message === 'string' ? message : message.before + message.title + message.after;
}

/** Puts words in front of a message, outside its title. */
export function prefixMessage(prefix: string, message: ToastMessage): ToastMessage {
  return typeof message === 'string'
    ? prefix + message
    : { ...message, before: prefix + message.before };
}

export interface Toast {
  id: number;
  message: ToastMessage;
  action?: ToastAction;
  /** False once the fade-out has started. */
  visible: boolean;
}

export interface Toasts {
  toast: Toast | null;
  show: (message: ToastMessage, action?: ToastAction) => void;
  dismiss: () => void;
}

export function useToasts(): Toasts {
  const [toast, setToast] = useState<Toast | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    for (const t of timers.current) clearTimeout(t);
    timers.current = [];
  }, []);

  const dismiss = useCallback(() => {
    clearTimers();
    setToast((t) => (t ? { ...t, visible: false } : null));
    timers.current.push(setTimeout(() => setToast(null), FADE_MS));
  }, [clearTimers]);

  const show = useCallback(
    (message: ToastMessage, action?: ToastAction) => {
      clearTimers();
      setToast({ id: Date.now(), message, action, visible: true });
      // Fade, then unmount, so the exit transition can actually run.
      timers.current.push(
        setTimeout(() => setToast((t) => (t ? { ...t, visible: false } : null)), LIFETIME_MS),
      );
      timers.current.push(setTimeout(() => setToast(null), LIFETIME_MS + FADE_MS));
    },
    [clearTimers],
  );

  useEffect(() => clearTimers, [clearTimers]);

  return { toast, show, dismiss };
}

export const TOAST_FADE_MS = FADE_MS;
